/**
 * «Del i sosiale medier», phase 1 (2026-10-22): who sees «Del», and the picture drawn in the browser.
 *
 * The rules (sizes, captions, what may be shared, where everything goes) are core's social-share.js; this file only
 * paints core's layout() on a canvas and hands back a JPEG. Nothing is sent anywhere: the person downloads it, copies
 * the text, or gives both to the phone's share sheet.
 */
import { API, blobUrl } from './api';
import { BannerCore, Printables, Social, type ShareLayout } from '../core';

export const SOCIAL_CAPABILITY = 'social_publishing';
export const SOCIAL_PERMISSION = 'notices.write';

/** «Del» is shown when the mosque HAS the capability and this person writes announcements. */
export const shows = (caps: string[] | null | undefined, can: (p: string) => boolean): boolean =>
  Boolean(caps && caps.includes(SOCIAL_CAPABILITY) && can(SOCIAL_PERMISSION));

export interface MosquePublic { name: string; shortName: string; website: string | null }
let mosqueOnce: Promise<MosquePublic> | null = null;
/** The mosque's public name and website (GET /mosque, the same answer the app reads), asked once per page. */
export function mosquePublic(): Promise<MosquePublic> {
  if (!mosqueOnce) {
    mosqueOnce = fetch(API + '/mosque').then((r) => (r.ok ? r.json() : {})).catch(() => ({}))
      .then((m: Record<string, unknown>) => ({
        name: typeof m.name === 'string' ? m.name : '', shortName: typeof m.shortName === 'string' ? m.shortName : '',
        website: typeof m.website === 'string' ? m.website : null,
      }));
  }
  return mosqueOnce;
}

/**
 * What is being shared: a text banner (template + layers), a picture banner, or an announcement.
 * Banner studio (2026-10-31): `decor` is the chosen ornament (null = the template's own); `photo` marks a «Bilde med
 * tekst» banner, whose picture is drawn behind the text — from `photoSrc` (a data: URL still in the editor) or, saved,
 * from the staff picture route by its id.
 */
export type ShareSource =
  | { kind: 'banner'; id: string; template: string; palette: string; layers: Record<string, string>; picture: boolean;
      decor?: string | null; photo?: boolean; photoSrc?: string | null }
  | { kind: 'notice'; id: string; title: string; body: string };

const loadImage = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const im = new Image();
  im.onload = () => resolve(im); im.onerror = () => reject(new Error('image')); im.src = src;
});

const headFont = () => (getComputedStyle(document.documentElement).getPropertyValue('--font-head') || '').trim() || 'system-ui, sans-serif';

/** A picture of iw×ih covering w×h (cropped at the edges, never squashed): { x, y, w, h }. */
export function cover(iw: number, ih: number, w: number, h: number): { x: number; y: number; w: number; h: number } {
  if (!(iw > 0 && ih > 0)) return { x: 0, y: 0, w, h };
  const k = Math.max(w / iw, h / ih);
  const cw = iw * k, ch = ih * k;
  return { x: (w - cw) / 2, y: (h - ch) / 2, w: cw, h: ch };
}

/** core's layout, painted: the ground, a photo covering it (a «Bilde med tekst» banner), each ornament (its own small SVG), then the text in its box. */
async function paintLayout(g: CanvasRenderingContext2D, L: ShareLayout, under: HTMLImageElement | null = null) {
  g.fillStyle = L.bg; g.fillRect(0, 0, L.w, L.h);
  if (under) { const c = cover(under.naturalWidth, under.naturalHeight, L.w, L.h); g.drawImage(under, c.x, c.y, c.w, c.h); }
  for (const p of L.pieces) {
    // A blob URL of the SVG that core made from fixed shapes — the same origin, so the canvas stays exportable.
    const url = URL.createObjectURL(new Blob([p.svg], { type: 'image/svg+xml' }));
    try { g.drawImage(await loadImage(url), p.x, p.y, p.w, p.h); } catch { /* an ornament that will not draw is left out */ } finally { URL.revokeObjectURL(url); }
  }
  const family = headFont();
  try { await document.fonts.ready; } catch { /* the fallback font */ }
  // The lines at full size, then smaller until they fit the box (the preview's CSS lets them overflow; a picture
  // cannot) and no word has to be broken in the middle («fredagsbøn / n») — that only happens at the smallest size.
  let k = 1, lines: { text: string; px: number; lh: number; l: ShareLayout['layers'][number] }[][] = [], total = 0;
  for (let i = 0; i < 16; i++) {
    let wordTooWide = false;
    lines = L.layers.map((l) => {
      const px = Math.max(12, Math.round(l.px * k));
      g.font = `${l.weight} ${px}px ${family}`;
      const measure = (s: string) => g.measureText(s).width;
      if (l.text.split(/\s+/).some((w) => measure(w) > L.box.w)) wordTooWide = true;
      return Social.wrap(l.text, L.box.w, measure).map((text) => ({ text, px, lh: px * l.lineHeight, l }));
    });
    total = lines.reduce((a, ls) => a + ls.reduce((b, x) => b + x.lh, 0), 0) + L.gap * Math.max(0, lines.length - 1);
    if (total <= L.box.h && !wordTooWide) break;
    k *= 0.92;
  }
  let y = L.box.y + Math.max(0, (L.box.h - total) / 2);
  g.textBaseline = 'middle';
  for (const ls of lines) {
    for (const x of ls) {
      g.font = `${x.l.weight} ${x.px}px ${family}`;
      g.fillStyle = x.l.colour;
      g.direction = x.l.dir === 'rtl' ? 'rtl' : 'ltr';
      const centre = L.align === 'center';
      g.textAlign = centre ? 'center' : x.l.dir === 'rtl' ? 'right' : 'left';
      const tx = centre ? L.box.x + L.box.w / 2 : x.l.dir === 'rtl' ? L.box.x + L.box.w : L.box.x;
      g.fillText(x.text, tx, y + x.lh / 2);
      y += x.lh;
    }
    y += L.gap;
  }
}

/**
 * The picture for one size, as a JPEG. A text banner and an announcement are drawn from their template; a picture
 * banner is the mosque's own poster, whole, on the dark ground the screens show it on. null when it cannot be made.
 */
export async function drawShareImage(src: ShareSource, sizeId: string, mosqueShort: string): Promise<Blob | null> {
  const canvas = await paint(src, Social.SIZES[sizeId] || Social.SIZES.square, mosqueShort, Social.PICTURE_GROUND);
  if (!canvas) return null;
  return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', Social.JPEG_QUALITY));
}

/**
 * A poster to print (MKK #34, 2026-10-29): the same drawing at A4 or A3 portrait, 200 dpi (core printables.POSTER_SIZES),
 * as a PNG (sharp text on paper), a picture banner on white paper rather than the screens' dark ground.
 */
export async function drawPoster(src: ShareSource, sizeId: string, mosqueShort: string): Promise<Blob | null> {
  const canvas = await paint(src, Printables.POSTER_SIZES[sizeId] || Printables.POSTER_SIZES.a4, mosqueShort, Printables.POSTER_GROUND);
  if (!canvas) return null;
  return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
}

/**
 * The screens' own shape (banner studio, 2026-10-31): the banner at 1920×1080 as a file — a PNG for «Last ned»
 * (sharp text), a JPEG to become a program's picture (the server re-encodes it anyway). null when it cannot be made.
 */
export async function drawScreenImage(src: ShareSource, mosqueShort: string, type: 'image/png' | 'image/jpeg' = 'image/jpeg'): Promise<Blob | null> {
  const canvas = await paint(src, Social.SCREEN, mosqueShort, Social.PICTURE_GROUND);
  if (!canvas) return null;
  return await new Promise<Blob | null>((resolve) => (type === 'image/png' ? canvas.toBlob(resolve, 'image/png') : canvas.toBlob(resolve, 'image/jpeg', Social.JPEG_QUALITY)));
}

/** The drawing itself, at `size`, with `ground` behind a picture banner: the canvas, or null when it cannot be made. */
async function paint(src: ShareSource, size: { w: number; h: number; text?: number }, mosqueShort: string, ground: string): Promise<HTMLCanvasElement | null> {
  const canvas = document.createElement('canvas');
  canvas.width = size.w; canvas.height = size.h;
  const g = canvas.getContext('2d');
  if (!g) return null;
  g.imageSmoothingQuality = 'high';
  try {
    if (src.kind === 'banner' && src.picture) {
      const url = await blobUrl(`/admin/banners/${encodeURIComponent(src.id)}/image`);
      if (!url) return null;
      try {
        const im = await loadImage(url);
        g.fillStyle = ground; g.fillRect(0, 0, size.w, size.h);
        const r = Social.contain(im.naturalWidth, im.naturalHeight, size.w, size.h);
        g.drawImage(im, r.x, r.y, r.w, r.h);
      } finally { URL.revokeObjectURL(url); }
    } else {
      const spec = src.kind === 'banner'
        ? { template: src.template, values: src.layers, palette: src.palette, decor: src.decor || null }
        : { ...Social.noticeBanner({ title_no: src.title }, mosqueShort), decor: null };
      const r = BannerCore.render(spec.template, spec.values, { palette: spec.palette, decor: spec.decor });
      const L = r && Social.layout(r, size);
      if (!L) return null;
      // A «Bilde med tekst» banner: its photo under the scrim and the text — the one still in the editor, or the saved one.
      let under: HTMLImageElement | null = null;
      if (src.kind === 'banner' && src.photo) {
        const url = src.photoSrc || await blobUrl(`/admin/banners/${encodeURIComponent(src.id)}/image`);
        if (!url) return null;
        try { under = await loadImage(url); } finally { if (!src.photoSrc) URL.revokeObjectURL(url); }
      }
      await paintLayout(g, L, under);
    }
    return canvas;
  } catch { return null; }
}

/** The title and text a caption is built from. A banner's are its headline, then its other lines. */
export function captionParts(src: ShareSource): { title: string; body: string } {
  if (src.kind === 'notice') return { title: src.title, body: src.body };
  const l = src.layers || {};
  return { title: l.headline || l.eyebrow || '', body: ['detail', 'when', 'where'].map((k) => (l[k] || '').trim()).filter(Boolean).join('\n') };
}

/** Text to the clipboard; on a browser without the clipboard API, the old way through a hidden field. */
export async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy'); ta.remove(); return ok;
  } catch { return false; }
}

/**
 * Phase 2 (2026-10-23): publishing straight to the mosque's Facebook Page and Instagram account. Shown where the mosque
 * has `social_meta` and the person writes announcements. Instagram takes square or portrait only — core social-meta.js
 * NETWORKS.instagram.sizes, pinned to this by test/social-share.test.mjs.
 */
export const DIRECT_CAPABILITY = 'social_meta';
export const showsDirect = (caps: string[] | null | undefined, can: (p: string) => boolean): boolean =>
  Boolean(caps && caps.includes(DIRECT_CAPABILITY) && can(SOCIAL_PERMISSION));
export const INSTAGRAM_SIZES = ['square', 'portrait'];

/** A Blob as base64, for the JSON body of POST /admin/social/publish. */
export function blobBase64(b: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => resolve(String(r.result || '').replace(/^data:[^,]*,/, ''));
    r.readAsDataURL(b);
  });
}
